import type {PropertyValue, ValueType} from '@github-ui/custom-properties-types'
import {Checkbox, FormControl, TextInput} from '@primer/react'

import {CustomPropertyMultiSelectPanel, CustomPropertySingleSelectPanel} from '../CustomPropertySelectPanel'
import styles from './RequiredDefinitionEditor.module.css'

interface Props {
  checkboxLabel?: string
  checkboxCaption?: string
  defaultValueCaption?: string
  isRequired: boolean
  valueType: ValueType
  defaultValue: PropertyValue
  defaultValueError?: string
  allowableDefaultValues: string[]
  propertyName: string
  onRequiredChange: (required: boolean) => void
  onDefaultValueChange: (value: PropertyValue) => void
  setDefaultValueInputRef?: (ref: HTMLElement | null) => void
  requiredFormExtension?: React.ReactNode
  className?: string
}

export function RequiredDefinitionEditor({
  checkboxLabel = 'Require this property for all targets',
  checkboxCaption = "Targets that don't have an explicit value for this property will inherit the default value.",
  defaultValueCaption = 'This is the default value that will be set for this property.',
  isRequired,
  valueType,
  defaultValue,
  defaultValueError,
  allowableDefaultValues: providedDefaultValues,
  propertyName,
  onRequiredChange,
  onDefaultValueChange,
  setDefaultValueInputRef,
  requiredFormExtension,
  className,
}: Props) {
  const isText = valueType === 'string'
  const isSingleSelect = valueType === 'single_select'
  const isTrueFalse = valueType === 'true_false'
  const isMultiSelect = valueType === 'multi_select'
  const isUrl = valueType === 'url'

  const defaultValueInvalid = !!defaultValueError
  const defaultValueAriaAriaProp = {
    'aria-invalid': defaultValueInvalid,
    'aria-labelledby': 'property-default-value-label',
    'aria-describedby': `${
      defaultValueInvalid ? 'property-default-value-validation' : ''
    } property-default-value-caption`,
  }

  const allowableDefaultValues = (isTrueFalse ? ['true', 'false'] : providedDefaultValues) || null

  return (
    <div className={className}>
      <FormControl>
        <Checkbox checked={isRequired} onChange={() => onRequiredChange(!isRequired)} />
        <FormControl.Label>{checkboxLabel}</FormControl.Label>
        <FormControl.Caption>{checkboxCaption}</FormControl.Caption>
      </FormControl>
      <div className={styles.optionsInputGroup}>
        {isRequired && (
          <>
            <FormControl className={styles.defaultValueFormControl}>
              <FormControl.Label id="property-default-value-label">Default value *</FormControl.Label>
              {isMultiSelect && (
                <div className={styles.selectPanelContainer}>
                  <CustomPropertyMultiSelectPanel
                    setRef={setDefaultValueInputRef}
                    propertyValue={defaultValue as string[]}
                    mixed={false}
                    onChange={values => onDefaultValueChange(values)}
                    propertyName={propertyName}
                    allowedValues={allowableDefaultValues}
                    defaultValue={null}
                    anchorProps={defaultValueAriaAriaProp}
                  />
                </div>
              )}

              {isSingleSelect && (
                <div className={styles.selectPanelContainer}>
                  <CustomPropertySingleSelectPanel
                    setRef={setDefaultValueInputRef}
                    propertyValue={defaultValue as string}
                    mixed={false}
                    onChange={values => onDefaultValueChange(values)}
                    propertyName={propertyName}
                    allowedValues={allowableDefaultValues}
                    defaultValue={null}
                    anchorProps={defaultValueAriaAriaProp}
                  />
                </div>
              )}

              {isTrueFalse && (
                <div className={styles.selectPanelContainer}>
                  <CustomPropertySingleSelectPanel
                    setRef={setDefaultValueInputRef}
                    propertyValue={defaultValue as string}
                    mixed={false}
                    onChange={onDefaultValueChange}
                    propertyName={propertyName}
                    allowedValues={['true', 'false']}
                    defaultValue={null}
                    anchorProps={defaultValueAriaAriaProp}
                  />
                </div>
              )}

              {(isText || isUrl) && (
                <TextInput
                  block
                  value={defaultValue}
                  ref={setDefaultValueInputRef as (ref: HTMLInputElement) => void}
                  onChange={e => onDefaultValueChange(e.target.value)}
                  {...defaultValueAriaAriaProp}
                />
              )}

              {defaultValueError && (
                <FormControl.Validation
                  id="property-default-value-validation"
                  variant="error"
                  className={styles.validationFeedback}
                >
                  {defaultValueError}
                </FormControl.Validation>
              )}
              <FormControl.Caption id="property-default-value-caption">{defaultValueCaption}</FormControl.Caption>
            </FormControl>
            {requiredFormExtension}
          </>
        )}
      </div>
    </div>
  )
}

try{ RequiredDefinitionEditor.displayName ||= 'RequiredDefinitionEditor' } catch {}